import * as THREE from 'three'

/**
 * Small geometry helpers shared by the rider and the scenery.
 *
 * Three ships BufferGeometryUtils for merging, but it lives in the addons
 * bundle and this is twenty lines of the part we actually use.
 */

/** Flood a geometry with one vertex colour. */
export function paint(g: THREE.BufferGeometry, r: number, gr: number, b: number): THREE.BufferGeometry {
  const count = g.attributes.position.count
  const colors = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    colors[i * 3] = r
    colors[i * 3 + 1] = gr
    colors[i * 3 + 2] = b
  }
  g.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  return g
}

/** Merge non-indexed geometries carrying position, normal and colour. */
export function mergeGeometries(list: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = list.map((g) => (g.index ? g.toNonIndexed() : g))
  let total = 0
  for (const g of flat) total += g.attributes.position.count

  const pos = new Float32Array(total * 3)
  const nor = new Float32Array(total * 3)
  const col = new Float32Array(total * 3)
  let o = 0
  for (const g of flat) {
    if (!g.attributes.normal) g.computeVertexNormals()
    const p = g.attributes.position.array as ArrayLike<number>
    const n = g.attributes.normal.array as ArrayLike<number>
    const c = g.attributes.color.array as ArrayLike<number>
    const len = g.attributes.position.count * 3
    for (let i = 0; i < len; i++) {
      pos[o + i] = p[i]
      nor[o + i] = n[i]
      col[o + i] = c[i]
    }
    o += len
    g.dispose()
  }

  const out = new THREE.BufferGeometry()
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3))
  out.setAttribute('color', new THREE.BufferAttribute(col, 3))
  out.computeBoundingSphere()
  return out
}

/** A box, coloured, positioned and optionally rotated, ready to merge. */
export function box(
  w: number, h: number, d: number,
  x: number, y: number, z: number,
  colour: readonly [number, number, number],
  rot?: { x?: number; y?: number; z?: number },
): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d)
  if (rot) {
    if (rot.x) g.rotateX(rot.x)
    if (rot.y) g.rotateY(rot.y)
    if (rot.z) g.rotateZ(rot.z)
  }
  g.translate(x, y, z)
  return paint(g, colour[0], colour[1], colour[2])
}

/**
 * A capped low-poly cylinder, coloured, positioned, optionally rotated.
 * Seven segments: round enough to read as a limb, cheap enough to forget.
 */
export function cyl(
  rTop: number, rBottom: number, h: number,
  x: number, y: number, z: number,
  colour: readonly [number, number, number],
  rot?: { x?: number; y?: number; z?: number },
  segments = 7,
): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTop, rBottom, h, segments)
  if (rot) {
    if (rot.x) g.rotateX(rot.x)
    if (rot.y) g.rotateY(rot.y)
    if (rot.z) g.rotateZ(rot.z)
  }
  g.translate(x, y, z)
  return paint(g, colour[0], colour[1], colour[2])
}

/** A low-poly sphere, coloured and positioned, ready to merge. */
export function sph(
  r: number,
  x: number, y: number, z: number,
  colour: readonly [number, number, number],
  scale?: { x?: number; y?: number; z?: number },
): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(r, 8, 6)
  if (scale) g.scale(scale.x ?? 1, scale.y ?? 1, scale.z ?? 1)
  g.translate(x, y, z)
  return paint(g, colour[0], colour[1], colour[2])
}

/**
 * A ski: a low-poly extrusion along +Z with a shovel at the tip, a kicked tail
 * and a waisted middle. Six segments is enough to read the profile in
 * silhouette, which is all it needs to do from behind at speed.
 */
export function ski(
  length: number, waist: number, colour: readonly [number, number, number],
): THREE.BufferGeometry {
  // t along the ski, half-width, and vertical rise of the base.
  const profile: Array<[t: number, halfWidth: number, rise: number]> = [
    [-0.50, waist * 0.42, 0.055], // tail, kicked up
    [-0.42, waist * 0.62, 0.012],
    [-0.20, waist * 0.52, 0.0],
    [0.16, waist * 0.50, 0.0],
    [0.38, waist * 0.66, 0.010], // shovel
    [0.47, waist * 0.52, 0.055],
    [0.50, waist * 0.30, 0.090], // tip
  ]
  const thickness = 0.028
  const parts: THREE.BufferGeometry[] = []

  for (let i = 0; i + 1 < profile.length; i++) {
    const [t0, w0, r0] = profile[i]
    const [t1, w1, r1] = profile[i + 1]
    const z0 = t0 * length
    const z1 = t1 * length
    const midZ = (z0 + z1) / 2
    const segLen = z1 - z0
    const midW = (w0 + w1)
    const midRise = (r0 + r1) / 2
    // Approximate each segment with a box, tilted to follow the rocker.
    const g = new THREE.BoxGeometry(midW, thickness, segLen)
    const tilt = Math.atan2(r1 - r0, segLen)
    g.rotateX(-tilt)
    g.translate(0, midRise + thickness / 2, midZ)
    parts.push(paint(g, colour[0], colour[1], colour[2]))
  }

  return mergeGeometries(parts)
}
